import React, { useState, useEffect } from 'react';
import { Button, Input, Modal, Tooltip } from 'antd';
import {
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axios/axiosInstance';
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
} from '../../components/ui';

export default function EpidemiologicalHistory() {
    const { t } = useTranslation();
    const [newName, setNewName] = useState('');
    const [records, setRecords] = useState([]);
    const [editingRecordId, setEditingRecordId] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        fetchRecords();
    }, []);

    const fetchRecords = async () => {
        try {
            const response = await axiosInstance.get("/epidemiological");
            setRecords(response.data.data);
        } catch (error) {
            console.error("Ошибка при получении списка эпидемии:", error);
        }
    };

    const createEpidemiologicalRecord = async () => {
        setIsLoading(true);
        try {
            await axiosInstance.post('/epidemiological', { name: newName });
            fetchRecords();
            closeModal();
        } catch (error) {
            console.error('Ошибка при создании записи эпидемиологической истории:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateEpidemiologicalRecord = async (recordId) => {
        setIsLoading(true);
        try {
            await axiosInstance.put(`/epidemiological/${recordId}`, { name: newName });
            fetchRecords();
            closeModal();
        } catch (error) {
            console.error('Ошибка при обновлении записи эпидемиологической истории:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteEpidemiologicalRecord = async (recordId) => {
        setIsLoading(true);
        try {
            await axiosInstance.delete(`/epidemiological/${recordId}`);
            fetchRecords();
        } catch (error) {
            console.error('Ошибка при удалении записи эпидемиологической истории:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setNewName('');
        setEditingRecordId(null);
    };

    const openModal = (record) => {
        setIsModalVisible(true);
        setNewName(record ? record.name : '');
        setEditingRecordId(record ? record.id : null);
    };

    const columns = [
        {
            title: t('common.id'),
            dataIndex: "id",
            key: "id",
            width: 80,
            render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
        },
        {
            title: t('common.name'),
            dataIndex: "name",
            key: "name",
            render: (name) => <span className="text-sm font-medium text-slate-800">{name}</span>,
        },
        {
            title: t('common.actions'),
            key: "action",
            width: 88,
            fixed: 'right',
            render: (text, record) => (
                <div className="flex items-center gap-0.5">
                    <Tooltip title={t('common.edit')}>
                        <RowIconButton onClick={() => openModal(record)}>
                            <PencilSquareIcon className="w-[18px] h-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                        <RowIconButton onClick={() => deleteEpidemiologicalRecord(record.id)}>
                            <TrashIcon className="w-[18px] h-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#1D2C7B]">{record.name}</span>
                <span className="block text-xs text-slate-500 mt-0.5 tabular-nums">{t('common.id')}: {record.id}</span>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
                <RowIconButton onClick={() => openModal(record)} aria-label={t('common.edit')}>
                    <PencilSquareIcon className="w-[18px] h-[18px]" />
                </RowIconButton>
                <RowIconButton onClick={() => deleteEpidemiologicalRecord(record.id)} aria-label={t('common.delete')}>
                    <TrashIcon className="w-[18px] h-[18px]" />
                </RowIconButton>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('epidem.title')}
                    count={records.length}
                    filters={
                        <Input
                            allowClear
                            size="small"
                            placeholder={t('common.search')}
                            prefix={<MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />}
                            className="md:!w-[220px]"
                        />
                    }
                    actions={
                        <ActionButton icon={<PlusIcon className="w-4 h-4" />} onClick={() => openModal(null)}>
                            {t('common.add')}
                        </ActionButton>
                    }
                />

                <DataTable
                    columns={columns}
                    dataSource={records}
                    loading={isLoading}
                    mobileCard={renderMobileCard}
                    scrollX={520}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                />
            </PageCard>

            <Modal
                centered
                width="100%"
                style={{ maxWidth: 520 }}
                title={editingRecordId ? t('epidem.edit') : t('epidem.create')}
                visible={isModalVisible}
                onCancel={closeModal}
                footer={[
                    <Button key="cancel" onClick={closeModal}>
                        {t('common.cancel')}
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={isLoading}
                        onClick={editingRecordId ? () => updateEpidemiologicalRecord(editingRecordId) : createEpidemiologicalRecord}
                    >
                        {t('common.save')}
                    </Button>,
                ]}
            >
                <Input
                    placeholder={t('epidem.name')}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                />
            </Modal>
        </PageShell>
    );
}
